import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/utils";

/**
 * Badge — Clones Design System (ADR-0030).
 *
 * Small pill, semantic-coloured. Variants map to the editorial palette:
 *   default     → Pulse (info / primary)
 *   secondary   → paper-3 (neutral chip)
 *   destructive → Bad
 *   outline     → ink text on hairline
 *   success     → Ok
 *   warning     → Warn
 */
const badgeVariants = cva(
  [
    "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors",
    "focus:outline-none focus:ring-2 focus:ring-[var(--pulse)] focus:ring-offset-2 focus:ring-offset-[var(--paper)]",
  ].join(" "),
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-[var(--pulse)] text-[var(--on-accent)] hover:bg-[var(--pulse)]/85",
        secondary:
          "border-transparent bg-[var(--paper-3)] text-[var(--ink)] hover:bg-[var(--paper-3)]/80",
        destructive:
          "border-transparent bg-[var(--bad)] text-[var(--on-accent)] hover:bg-[var(--bad)]/85",
        outline:
          "border-[var(--line-strong)] text-[var(--ink)]",
        success:
          "border-transparent bg-[var(--ok)] text-[var(--on-accent)] hover:bg-[var(--ok)]/85",
        warning:
          "border-transparent bg-[var(--warn)] text-[var(--on-warning)] hover:bg-[var(--warn)]/85",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {}

const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant, ...props }, ref) => (
    <span ref={ref} className={cn(badgeVariants({ variant }), className)} {...props} />
  ),
);
Badge.displayName = "Badge";

export { Badge, badgeVariants };
